import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import {
  autodetectVendorByCui,
  getVendorLogoUrl,
} from "../../services/scadentarFurnizoriApi";

const initialVendor = {
  denumire: "",
  cod_fiscal: "",
  vendor_scope: "DOMESTIC",
  vendor_category: "SERVICII",
  reg_comertului: "",
  email: "",
  telefon: "",
  website: "",
  persoana_contact: "",
  adresa: "",
  localitate: "",
  judet: "",
  tara: "RO",
  banca: "",
  iban: "",
  kyc_notes: "",
};

const vendorScopeOptions = [
  { value: "DOMESTIC", label: "Domestic" },
  { value: "INTRA_EU", label: "Intra EU" },
  { value: "NON_EU", label: "NON EU" },
];

const vendorCategoryOptions = [
  { value: "SERVICII", label: "Servicii" },
  { value: "BUNURI", label: "Bunuri" },
  { value: "IMPORT_BUNURI", label: "Import bunuri" },
  { value: "IMPORT_SERVICII", label: "Import servicii" },
  { value: "UTILITATI", label: "Utilitati" },
  { value: "CHIRIE", label: "Chirie" },
  { value: "SOFTWARE_SAAS", label: "Software / SaaS" },
];

function getOptionLabel(options, value) {
  return options.find((x) => x.value === value)?.label || "-";
}

function VendorsTab({
  vendors,
  searchTerm,
  onSearchChange,
  onCreateVendor,
  onOpenVendor,
  onDeleteVendor,
  onRefresh,
  onBackfill,
}) {
  const [form, setForm] = useState(initialVendor);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoError, setAutoError] = useState("");
  const [autoMeta, setAutoMeta] = useState(null);
  const [deletingVendorId, setDeletingVendorId] = useState(null);
  const canRunAutodetect = Boolean(
    String(form.cod_fiscal || "").trim() || String(form.denumire || "").trim()
  );

  const runAutodetect = async () => {
    try {
      setAutoError("");
      setAutoMeta(null);
      setAutoLoading(true);
      const response = await autodetectVendorByCui({
        cod_fiscal: form.cod_fiscal,
        denumire: form.denumire,
        tara: form.tara,
        current: form,
      });
      const payload = response?.data?.data || {};
      setForm((prev) => ({ ...prev, ...payload }));
      setAutoMeta({
        source: response?.data?.source || "ANAF",
        model: response?.data?.model || null,
        confidence: response?.data?.data?.confidence,
      });
    } catch (err) {
      setAutoError(
          err?.response?.data?.error ||
          err?.message ||
          "AI autocomplete furnizor a esuat."
      );
    } finally {
      setAutoLoading(false);
    }
  };

  return (
    <Box className="sf-vendors-grid">
      <Paper className="sf-form-card sf-vendor-create-card">
        <Typography className="sf-section-title">
          <AddBusinessRoundedIcon fontSize="small" /> Furnizor nou
        </Typography>
        <Typography className="sf-cell-secondary">
          Introdu denumire sau cod fiscal/VAT/tax id si foloseste AI pentru completare globala.
        </Typography>
        {autoError ? <Alert severity="error">{autoError}</Alert> : null}
        {autoMeta ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip size="small" color="info" label={`Sursa: ${autoMeta.source}`} />
            {autoMeta.model ? (
              <Chip size="small" variant="outlined" label={`Model: ${autoMeta.model}`} />
            ) : null}
            {autoMeta.confidence !== null && autoMeta.confidence !== undefined ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Confidence: ${Math.round(Number(autoMeta.confidence) * 100)}%`}
              />
            ) : null}
          </Stack>
        ) : null}
        <Divider />
        <TextField
          label="Denumire"
          value={form.denumire}
          onChange={(e) => setForm((s) => ({ ...s, denumire: e.target.value }))}
        />
        <TextField
          label="Cod fiscal"
          value={form.cod_fiscal}
          onChange={(e) => setForm((s) => ({ ...s, cod_fiscal: e.target.value }))}
          placeholder="RO43428737 / DE123456789 / US tax id"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BadgeRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  size="small"
                  className="sf-cui-autodetect-btn"
                  startIcon={
                    autoLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AutoAwesomeRoundedIcon fontSize="small" />
                    )
                  }
                  disabled={autoLoading || !canRunAutodetect}
                  onClick={runAutodetect}
                >
                  AI complete
                </Button>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label="Zona"
          value={form.vendor_scope}
          onChange={(e) => setForm((s) => ({ ...s, vendor_scope: e.target.value }))}
        >
          {vendorScopeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Tip furnizor"
          value={form.vendor_category}
          onChange={(e) => setForm((s) => ({ ...s, vendor_category: e.target.value }))}
        >
          {vendorCategoryOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Email"
          value={form.email}
          onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
        />
        <TextField
          label="Telefon"
          value={form.telefon}
          onChange={(e) => setForm((s) => ({ ...s, telefon: e.target.value }))}
        />
        <TextField
          label="Website"
          value={form.website}
          onChange={(e) => setForm((s) => ({ ...s, website: e.target.value }))}
        />
        <TextField
          label="Persoana contact"
          value={form.persoana_contact}
          onChange={(e) => setForm((s) => ({ ...s, persoana_contact: e.target.value }))}
        />
        <TextField
          label="Adresa"
          value={form.adresa}
          onChange={(e) => setForm((s) => ({ ...s, adresa: e.target.value }))}
        />
        <Button
          variant="contained"
          className="sf-btn-primary"
          onClick={async () => {
            await onCreateVendor(form);
            setForm(initialVendor);
            setAutoMeta(null);
            setAutoError("");
          }}
        >
          Salveaza furnizor
        </Button>
      </Paper>

      <Paper className="sf-table-shell">
        <Stack direction="row" justifyContent="space-between" alignItems="center" className="sf-inline-actions">
          <TextField
            size="small"
            label="Cauta furnizor"
            value={searchTerm || ""}
            onChange={(e) => onSearchChange?.(e.target.value)}
            InputProps={{
              startAdornment: <SearchRoundedIcon fontSize="small" style={{ marginRight: 6 }} />,
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={onRefresh}>Refresh</Button>
          </Stack>
        </Stack>

        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Furnizor</TableCell>
              <TableCell>CUI</TableCell>
              <TableCell>Facturi</TableCell>
              <TableCell>Sold deschis (RON)</TableCell>
              <TableCell>Achitat (RON)</TableCell>
              <TableCell align="right">Actiune</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(vendors || []).map((v) => (
              <TableRow key={v.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Avatar src={getVendorLogoUrl(v.id)}>{(v.denumire || "?")[0]}</Avatar>
                    <Box>
                      <Typography className="sf-cell-primary">{v.denumire}</Typography>
                      <Typography className="sf-cell-secondary">
                        {(v.email || "fara email")}
                        {" | "}
                        {getOptionLabel(vendorScopeOptions, v.vendor_scope)}
                        {" | "}
                        {getOptionLabel(vendorCategoryOptions, v.vendor_category)}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>{v.cod_fiscal || "-"}</TableCell>
                <TableCell>{Number(v.invoices_count || 0)}</TableCell>
                <TableCell>{Number(v.open_balance_ron || 0).toFixed(2)}</TableCell>
                <TableCell>{Number(v.paid_total_ron || 0).toFixed(2)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => onOpenVendor?.(v)}>
                      Deschide fisa
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={
                        Number(deletingVendorId) === Number(v.id) ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <DeleteRoundedIcon fontSize="small" />
                        )
                      }
                      disabled={Boolean(deletingVendorId) || !onDeleteVendor}
                      onClick={async (event) => {
                        event.stopPropagation();
                        const label = v.denumire || v.cod_fiscal || v.id;
                        if (!window.confirm(`Stergi furnizorul ${label}? Facturile lui nu se sterg.`)) return;
                        try {
                          setDeletingVendorId(v.id);
                          await onDeleteVendor(v);
                        } catch (err) {
                          window.alert(err?.response?.data?.error || err?.message || "Nu am putut sterge furnizorul.");
                        } finally {
                          setDeletingVendorId(null);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!(vendors || []).length && (
              <TableRow>
                <TableCell colSpan={6} align="center">Nu exista furnizori.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export default VendorsTab;
